/**
 * PEÇAS DE VIDRO COMPARTILHADAS ENTRE AS ABAS (pedido 19/10: "Resumo, Erros e
 * Análise no padrão do Processos, com layout e cores").
 *
 * O Processos já tinha o vocabulário visual — fundo colorido suave atrás e
 * cartões de vidro (`VIDRO`, do NoBoard) por cima. Ele vivia dentro da aba; aqui
 * ele vira peça comum para as outras abas não reinventarem um vidro parecido e
 * diferente. `VIDRO` continua morando no NoBoard (é dele a primeira versão) e é
 * só reexportado.
 *
 * Cada aba tem um MATIZ próprio (`MATIZ_DA_ABA`): é o que pinta o ícone, a aba
 * ativa do menu e o fundo dela. A cor diz "onde estou" sem precisar ler o título.
 */

import React from 'react';
import { VIDRO } from './NoBoard';
import { C, FONTE_MONO, TONES, type Tone } from '../paleta';

export { VIDRO };

export type ChaveAba = 'analise' | 'processos' | 'resumo' | 'erros' | 'dependencias';

/** Matiz oklch de cada aba — violeta, verde, azul, vermelho e âmbar. */
export const MATIZ_DA_ABA: Record<ChaveAba, number> = {
  analise: 295,
  processos: 162,
  resumo: 240,
  erros: 25,
  dependencias: 70,
};

/**
 * Fundo da aba — o mesmo desenho do `FUNDO_DO_BOARD` do Processos (três manchas
 * radiais sobre um verde-gelo), com a primeira mancha no matiz da aba.
 */
export const fundoDaAba = (aba: ChaveAba): React.CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  padding: 'clamp(14px, 2vw, 30px)',
  borderRadius: '26px',
  background:
    `radial-gradient(900px 420px at 0% 0%, oklch(0.9 0.08 ${MATIZ_DA_ABA[aba]} / 0.7), transparent 62%), ` +
    'radial-gradient(820px 460px at 100% 8%, oklch(0.9 0.07 235 / 0.55), transparent 60%), ' +
    'radial-gradient(900px 520px at 55% 105%, oklch(0.93 0.08 95 / 0.5), transparent 62%), ' +
    'oklch(0.972 0.01 165)',
});

/** Cartão de vidro padrão das abas. */
export const CARTAO: React.CSSProperties = {
  ...VIDRO,
  borderRadius: '18px',
  padding: '18px 20px',
};

/** Rótulo pequeno em caixa alta (eyebrow) — o mesmo das seções do Processos. */
export const EYEBROW: React.CSSProperties = {
  fontFamily: FONTE_MONO,
  fontSize: '10.5px',
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: C.sub,
};

/**
 * Título de seção com o selo colorido da aba à esquerda. `direita` é o lugar de
 * um contador ou ação, alinhado ao fim da linha.
 */
export const TituloSecao: React.FC<{
  aba: ChaveAba;
  icone: React.ReactNode;
  titulo: React.ReactNode;
  sub?: React.ReactNode;
  direita?: React.ReactNode;
}> = ({ aba, icone, titulo, sub, direita }) => {
  const h = MATIZ_DA_ABA[aba];
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
      <span
        aria-hidden
        style={{
          flex: 'none',
          width: '34px',
          height: '34px',
          borderRadius: '11px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          background: `linear-gradient(140deg, oklch(0.68 0.15 ${h}), oklch(0.52 0.15 ${h}))`,
          boxShadow: `0 6px 16px oklch(0.55 0.15 ${h} / 0.3)`,
        }}
      >
        {icone}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14.5px', fontWeight: 700, color: C.ink }}>{titulo}</div>
        {sub && (
          <div
            style={{ fontSize: '12px', color: C.sub, marginTop: '3px', textWrap: 'pretty' } as any}
          >
            {sub}
          </div>
        )}
      </div>
      {direita && <div style={{ flex: 'none' }}>{direita}</div>}
    </div>
  );
};

/**
 * Faixa de aviso com tom semântico (ok/err/warn/run/idle) — substitui as caixas
 * de borda colorida que cada aba desenhava à mão.
 */
export const Faixa: React.FC<{
  tom: Tone;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ tom, children, style }) => {
  const t = TONES[tom];
  return (
    <div
      style={
        {
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          borderRadius: '12px',
          padding: '10px 14px',
          fontSize: '12px',
          color: t.fg,
          background: `color-mix(in oklch, ${t.bg} 82%, transparent)`,
          border: `1px solid color-mix(in oklch, ${t.dot} 35%, transparent)`,
          textWrap: 'pretty',
          ...style,
        } as React.CSSProperties
      }
    >
      <span
        aria-hidden
        style={{
          flex: 'none',
          width: '7px',
          height: '7px',
          borderRadius: '50%',
          marginTop: '5px',
          background: t.dot,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
};

/** Botão-pílula de vidro (presets, alternâncias). `ativo` pinta no matiz da aba. */
export const estiloPilula = (ativo: boolean, aba: ChaveAba): React.CSSProperties => {
  const h = MATIZ_DA_ABA[aba];
  return {
    padding: '6px 13px',
    borderRadius: '999px',
    border: ativo ? `1px solid oklch(0.55 0.14 ${h})` : '1px solid rgba(255,255,255,0.9)',
    background: ativo
      ? `linear-gradient(140deg, oklch(0.62 0.14 ${h}), oklch(0.5 0.14 ${h}))`
      : 'rgba(255,255,255,0.6)',
    color: ativo ? 'white' : C.ink,
    fontWeight: ativo ? 600 : 500,
    fontSize: '12px',
    cursor: 'pointer',
    boxShadow: ativo ? `0 4px 12px oklch(0.55 0.14 ${h} / 0.28)` : '0 1px 2px rgba(16,60,48,0.06)',
    whiteSpace: 'nowrap',
  };
};

/** Campo de vidro (input/select) — mesma altura das pílulas. */
export const CAMPO: React.CSSProperties = {
  padding: '7px 11px',
  borderRadius: '10px',
  border: '1px solid rgba(255,255,255,0.95)',
  background: 'rgba(255,255,255,0.72)',
  boxShadow: 'inset 0 1px 2px rgba(16,60,48,0.06)',
  fontSize: '12.5px',
  color: C.ink,
  outline: 'none',
};

/**
 * Ícones de traço próprios (SVG inline, 24×24, `currentColor`). Não usamos o
 * phosphor aqui: nenhuma peça do orquestrador dependia dele, e um pacote ESM a
 * mais no Jest do CRA pede transformação que os testes desta pasta não têm.
 */
const TRACOS: Record<string, string> = {
  analise: 'M4 19V5M4 19h16M8 15l3-4 3 2 5-6',
  processos: 'M5 6h6v4H5zM13 14h6v4h-6zM8 10v4h5M16 10V6h-5',
  resumo: 'M4 6h2M4 12h2M4 18h2M9 6h11M9 12h11M9 18h11',
  erros: 'M12 3l9 16H3zM12 10v4M12 17h.01',
  dependencias: 'M12 4v5M12 9l-6 5M12 9l6 5M6 14v5M18 14v5',
  busca: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4',
  empresa: 'M4 20V6l8-3v17M12 9h8v11M7 9h2M7 13h2M7 17h2M15 13h2M15 17h2M2 20h20',
  relogio: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2',
  raio: 'M13 2L4 14h7l-1 8 9-12h-7z',
  funil: 'M3 5h18l-7 8v6l-4 2v-8z',
  check: 'M5 12l5 5 9-10',
  x: 'M6 6l12 12M18 6L6 18',
  alerta: 'M12 3l9 16H3zM12 10v4M12 17h.01',
  seta: 'M5 12h14M13 6l6 6-6 6',
  calendario: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
};

export type NomeIcone = keyof typeof TRACOS;

export const Icone: React.FC<{
  nome: NomeIcone;
  tamanho?: number;
  style?: React.CSSProperties;
}> = ({ nome, tamanho = 18, style }) => (
  <svg
    aria-hidden
    width={tamanho}
    height={tamanho}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flex: 'none', display: 'block', ...style }}
  >
    <path d={TRACOS[nome]} />
  </svg>
);
